// Sons da mesa, sintetizados com WebAudio: nenhum arquivo para baixar. O navegador só libera o áudio
// depois de um toque ou tecla, então o contexto nasce no primeiro gesto (desbloquear).
import { preferencias } from './preferencias.ts';

type Onda = OscillatorType;
let ctx: AudioContext | null = null;
let saida: GainNode | null = null;
let ruidoBuf: AudioBuffer | null = null;

// Registro do que tocou, só fora de produção: os testes de ponta a ponta conferem por aqui.
const registro: string[] | null = import.meta.env.PROD ? null : ((globalThis as { __sons?: string[] }).__sons ??= []);
function anotar(nome: string): void { registro?.push(nome); }

function contexto(): AudioContext | null {
  if (ctx) return ctx;
  const C = globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!C) return null;
  ctx = new C();
  const comp = ctx.createDynamicsCompressor();
  saida = ctx.createGain();
  saida.gain.value = 0.7;
  saida.connect(comp).connect(ctx.destination);
  ruidoBuf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const d = ruidoBuf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return ctx;
}

// Chamado no primeiro toque ou tecla em qualquer lugar do jogo.
export function desbloquear(): void {
  const c = contexto();
  if (c?.state === 'suspended') void c.resume();
  try {
    if (globalThis.speechSynthesis && !desbloqueouVoz) {
      desbloqueouVoz = true;
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      speechSynthesis.speak(u);
    }
  } catch { /* sem voz sintética */ }
}
let desbloqueouVoz = false;

function pronto(): AudioContext | null {
  if (!preferencias().som) return null;
  const c = contexto();
  return c && c.state === 'running' ? c : null;
}

function ruido(c: AudioContext, t: number, dur: number, filtro: BiquadFilterType, freq: number, ganho: number, q = 1): void {
  const src = c.createBufferSource(), f = c.createBiquadFilter(), g = c.createGain();
  src.buffer = ruidoBuf;
  f.type = filtro; f.frequency.value = freq; f.Q.value = q;
  g.gain.setValueAtTime(ganho, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  src.connect(f).connect(g).connect(saida!);
  src.start(t, Math.random() * 0.5, dur + 0.05);
}

function tom(c: AudioContext, t: number, freq: number, dur: number, onda: Onda, ganho: number, ate?: number): void {
  const o = c.createOscillator(), g = c.createGain();
  o.type = onda;
  o.frequency.setValueAtTime(freq, t);
  if (ate) o.frequency.exponentialRampToValueAtTime(ate, t + dur);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(ganho, t + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(saida!);
  o.start(t);
  o.stop(t + dur + 0.05);
}

const NOTA = (n: number) => 440 * 2 ** ((n - 69) / 12); // número MIDI para Hz

export const som = {
  // carta batendo na mesa
  carta(): void {
    anotar('carta');
    const c = pronto(); if (!c) return;
    const t = c.currentTime;
    ruido(c, t, 0.07, 'bandpass', 1800, 0.9, 0.8);
    tom(c, t, 120, 0.08, 'sine', 0.35, 60);
  },
  cartaCoberta(): void {
    anotar('cartaCoberta');
    const c = pronto(); if (!c) return;
    const t = c.currentTime;
    ruido(c, t, 0.08, 'lowpass', 900, 0.6);
    tom(c, t, 90, 0.09, 'sine', 0.3, 50);
  },
  distribuir(): void {
    anotar('distribuir');
    const c = pronto(); if (!c) return;
    ruido(c, c.currentTime, 0.035, 'highpass', 3000, 0.25);
  },
  embaralhar(): void {
    anotar('embaralhar');
    const c = pronto(); if (!c) return;
    let t = c.currentTime;
    for (let i = 0; i < 22; i++) { ruido(c, t, 0.03, 'bandpass', 2500 + Math.random() * 800, 0.15 + Math.random() * 0.2, 1.2); t += 0.025 + Math.random() * 0.012; }
  },
  suaVez(): void {
    anotar('suaVez');
    const c = pronto(); if (!c) return;
    const t = c.currentTime;
    tom(c, t, NOTA(76), 0.14, 'sine', 0.22);
    tom(c, t + 0.12, NOTA(81), 0.2, 'sine', 0.22);
  },
  tique(): void {
    anotar('tique');
    const c = pronto(); if (!c) return;
    tom(c, c.currentTime, 1250, 0.05, 'square', 0.06);
  },
  rodada(nossa: boolean): void {
    anotar(nossa ? 'rodadaNossa' : 'rodadaDeles');
    const c = pronto(); if (!c) return;
    const t = c.currentTime;
    if (nossa) [72, 76, 79].forEach((n, i) => tom(c, t + i * 0.08, NOTA(n), 0.22, 'triangle', 0.22));
    else [67, 62].forEach((n, i) => tom(c, t + i * 0.12, NOTA(n), 0.25, 'triangle', 0.16));
  },
  empachou(): void {
    anotar('empachou');
    const c = pronto(); if (!c) return;
    const t = c.currentTime;
    for (const d of [0, 0.16]) { ruido(c, t + d, 0.06, 'bandpass', 900, 0.7, 2); tom(c, t + d, 320, 0.07, 'sine', 0.3, 250); }
  },
  zap(): void {
    anotar('zap');
    const c = pronto(); if (!c) return;
    const t = c.currentTime;
    [67, 72, 76, 79, 84].forEach((n, i) => tom(c, t + i * 0.07, NOTA(n), 0.3, 'sawtooth', 0.09));
    tom(c, t + 0.35, NOTA(84), 0.7, 'triangle', 0.2);
    tom(c, t + 0.35, NOTA(88), 0.7, 'triangle', 0.14);
  },
  aceitou(): void {
    anotar('aceitou');
    const c = pronto(); if (!c) return;
    tom(c, c.currentTime, NOTA(84), 0.6, 'sine', 0.2);
  },
  correu(): void {
    anotar('correu');
    const c = pronto(); if (!c) return;
    tom(c, c.currentTime, 620, 0.38, 'triangle', 0.2, 180);
  },
  // mão de onze ou mão de ferro: rufar de tambor
  especial(): void {
    anotar('especial');
    const c = pronto(); if (!c) return;
    const t = c.currentTime;
    for (let i = 0; i < 16; i++) ruido(c, t + i * 0.05, 0.06, 'lowpass', 500, 0.15 + i * 0.03);
    ruido(c, t + 0.82, 0.3, 'lowpass', 300, 0.9);
    tom(c, t + 0.82, 80, 0.35, 'sine', 0.5, 45);
  },
  vitoria(): void {
    anotar('vitoria');
    const c = pronto(); if (!c) return;
    const t = c.currentTime;
    [72, 76, 79].forEach((n, i) => tom(c, t + i * 0.13, NOTA(n), 0.25, 'triangle', 0.22));
    for (const n of [72, 76, 79, 84]) tom(c, t + 0.42, NOTA(n), 1.1, 'triangle', 0.13);
  },
  derrota(): void {
    anotar('derrota');
    const c = pronto(); if (!c) return;
    const t = c.currentTime;
    [67, 66, 65].forEach((n, i) => tom(c, t + i * 0.3, NOTA(n), 0.3, 'triangle', 0.18));
    tom(c, t + 0.9, NOTA(64), 0.9, 'triangle', 0.18, NOTA(61));
  },
  pop(): void {
    anotar('pop');
    const c = pronto(); if (!c) return;
    tom(c, c.currentTime, 520, 0.07, 'sine', 0.14, 900);
  },
};

// Vibração curta no celular, quando o som está ligado.
export function vibrar(ms: number): void {
  if (!preferencias().som) return;
  try { navigator.vibrate?.(ms); } catch { /* sem vibração */ }
}

// Toca uma gravação já decodificada, com o volume normalizado pelo pico.
export function tocarGravacao(buf: AudioBuffer): boolean {
  const c = pronto(); if (!c) return false;
  let pico = 0;
  for (let ch = 0; ch < buf.numberOfChannels; ch++) { const d = buf.getChannelData(ch); for (let i = 0; i < d.length; i += 4) pico = Math.max(pico, Math.abs(d[i]!)); }
  const src = c.createBufferSource(), g = c.createGain();
  src.buffer = buf;
  g.gain.value = pico > 0.01 ? Math.min(4, 0.9 / pico) : 1;
  src.connect(g).connect(saida!);
  src.start();
  return true;
}

// Decodifica um arquivo de áudio (grito gravado) para tocar sem atraso.
export async function decodificar(dados: ArrayBuffer): Promise<AudioBuffer | null> {
  const c = contexto();
  if (!c) return null;
  try { return await c.decodeAudioData(dados); } catch { return null; }
}

export { anotar };
